import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { PERMISSIONS } from '../../../constants/permissions';
import { DashboardService } from '../../../services/dashboard.service';
import { PeopleSummary, HeadcountPoint } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import HBars from '../charts/HBars';
import Columns from '../charts/Columns';
import StatTile from '../charts/StatTile';
import useKpiTargets from '../useKpiTargets';

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW;

const PeopleWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<PeopleSummary | null>(null);
    const targets = useKpiTargets(institutionId);
    useEffect(() => {
        let live = true;
        DashboardService.getPeopleSummary(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    const headcountTarget = targets['people.headcount'];
    const headcountMeter = headcountTarget != null && headcountTarget > 0
        ? Math.max(0, Math.min(100, (d.active / headcountTarget) * 100))
        : undefined;
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label="Active" value={d.active} sub={`of ${d.total}`} tone="good" target={headcountTarget} meter={headcountMeter} />
                <StatTile label="Inactive" value={d.inactive} />
                <StatTile label="No department" value={d.noDepartmentCount} tone={d.noDepartmentCount > 0 ? 'warning' : 'default'} />
            </Stack>
            <HBars data={d.byDepartment.map(b => ({ label: b.label, value: b.count }))} colorIndex={2} />
        </Box>
    );
};

const loadPeopleGrowth = (institutionId: string): Promise<HeadcountPoint[]> =>
    DashboardService.getPeopleGrowth(institutionId);

const PeopleGrowthWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadPeopleGrowth, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The series is cumulative and month-zero-filled server-side; an empty array only happens
    // when the institution has never had a member (entity-absence, decision 14).
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No members have joined this institution yet.
            </Typography>
        );
    }
    return (
        <Box>
            <Columns data={d.map(p => ({ label: p.month, value: p.headcount }))} colorIndex={2} />
            <Typography variant="caption" color="text.secondary">Cumulative members joined, including any since deactivated</Typography>
        </Box>
    );
};

export const peopleWidgets: WidgetDef[] = [
    { id: 'people.summary', module: 'people', title: 'People — headcount & departments', subtitle: 'Active headcount and where people sit by department', permission: DASHBOARD_PERMISSION, size: 'md', component: PeopleWidget },
    { id: 'people.growth', module: 'people', title: 'People — headcount growth', subtitle: 'Cumulative members joined, month by month', permission: DASHBOARD_PERMISSION, size: 'lg', component: PeopleGrowthWidget },
];
